import axios from 'axios';
import { API_URL } from '../../config/api';
import { PhoneConfig, ResolvedParty, CallLogRow, CallDirection } from './types';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export async function fetchPhoneConfig(): Promise<PhoneConfig> {
  const r = await axios.get(`${API_URL}/api/telephony/config`, { headers: auth() });
  return r.data;
}

export async function resolveNumber(number: string): Promise<ResolvedParty> {
  const r = await axios.get(`${API_URL}/api/telephony/resolve`, {
    params: { number }, headers: auth(),
  });
  return r.data;
}

export async function openCallLog(direction: CallDirection, number: string, pbxCallId?: string, status?: string) {
  const r = await axios.post(`${API_URL}/api/telephony/calls`,
    { direction, number, pbxCallId, status }, { headers: auth() });
  return r.data as { id: string };
}

export async function updateCallLog(
  id: string,
  patch: { status?: string; durationSec?: number; recordingUrl?: string; audioStats?: import('./callStats').AudioStatsSummary },
) {
  const r = await axios.patch(`${API_URL}/api/telephony/calls/${id}`, patch, { headers: auth() });
  return r.data;
}

export interface CallQuery {
  customerId?: string;
  dealId?: string;
  mine?: boolean;
  direction?: 'inbound' | 'outbound';
  status?: string;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchCalls(params: CallQuery) {
  const r = await axios.get(`${API_URL}/api/telephony/calls`, {
    params: { ...params, mine: params.mine ? '1' : undefined }, headers: auth(),
  });
  return r.data as { calls: CallLogRow[]; total: number; page: number; pageSize: number };
}

export async function fetchMissedCount(): Promise<number> {
  const r = await axios.get(`${API_URL}/api/telephony/calls/missed-count`, { headers: auth() });
  return Number(r.data?.count) || 0;
}

/** Upload a call recording blob; the server stores it privately in Spaces. */
export async function uploadRecording(callLogId: string, blob: Blob) {
  const form = new FormData();
  const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
  form.append('audio', blob, `${callLogId}.${ext}`);
  await axios.post(`${API_URL}/api/telephony/calls/${callLogId}/recording`, form, {
    headers: { ...auth() }, // browser sets the multipart boundary
  });
}

export interface TelephonyStats {
  range: { from: string; to: string };
  totals: {
    total: number; inbound: number; outbound: number; answered: number; missed: number;
    answerRate: number; avgTalkSec: number; totalTalkSec: number;
  };
  byUser: { userId: string; name: string; total: number; answered: number; answerRate: number; avgTalkSec: number }[];
  trend: { day: string; count: number }[];
}

export async function fetchStats(params: { from?: string; to?: string }): Promise<TelephonyStats> {
  const r = await axios.get(`${API_URL}/api/telephony/stats`, { params, headers: auth() });
  return r.data;
}

export interface DialCandidate {
  id: string;
  fullName: string;
  phone: string;
  type: string;
  company: string | null;
}

/** Search customers by name/phone for the "dial by name" picker. */
export async function searchCustomers(q: string): Promise<DialCandidate[]> {
  const r = await axios.get(`${API_URL}/api/telephony/search`, { params: { q }, headers: auth() });
  return r.data.results as DialCandidate[];
}

// ─── Call screening + DND ──────────────────────────────────────────
export async function fetchAllowedCallers() {
  const r = await axios.get(`${API_URL}/api/telephony/screening/allowed`, { headers: auth() });
  return r.data.callers as import('./types').AllowedCaller[];
}
export async function setDnd(dnd: boolean) {
  const r = await axios.put(`${API_URL}/api/telephony/dnd`, { dnd }, { headers: auth() });
  return !!r.data?.dnd;
}
export async function setScreeningEnabled(screeningEnabled: boolean) {
  const r = await axios.put(`${API_URL}/api/telephony/screening`, { screeningEnabled }, { headers: auth() });
  return !!r.data?.screeningEnabled;
}
export async function addAllowedCaller(number: string, label?: string) {
  const r = await axios.post(`${API_URL}/api/telephony/screening/allowed`, { number, label }, { headers: auth() });
  return r.data;
}
export async function removeAllowedCaller(id: string) {
  await axios.delete(`${API_URL}/api/telephony/screening/allowed/${id}`, { headers: auth() });
}

// ─── Extension admin (super_admin) ─────────────────────────────────
/** Move an employee's extension (same number + secret) to another employee. */
export async function transferExtension(userId: string, targetUserId: string) {
  const r = await axios.post(
    `${API_URL}/api/telephony/extensions/${userId}/transfer`,
    { targetUserId }, { headers: auth() });
  return r.data;
}
/** Unassign (remove) an employee's extension. */
export async function unassignExtension(userId: string) {
  await axios.delete(`${API_URL}/api/telephony/extensions/${userId}`, { headers: auth() });
}

/** Save a quick note (written during a call) to the customer's notes. */
export async function addCustomerNote(customerId: string, content: string) {
  await axios.post(`${API_URL}/api/customers/${customerId}/notes`, { content }, { headers: auth() });
}

/** Short-lived signed URL to play a call's recording. */
export async function fetchRecordingUrl(callLogId: string): Promise<string | null> {
  try {
    const r = await axios.get(`${API_URL}/api/telephony/calls/${callLogId}/recording`, { headers: auth() });
    return r.data?.url || null;
  } catch {
    return null;
  }
}

/** Auto-SMS to inbound callers (super_admin). The token is never returned — the
 *  server sends `smsTokenSet` so the field can show "saved" and stay blank. */
export type CallerSmsSettings = {
  smsOnCallEnabled: boolean;
  smsTemplate: string;
  smsUsername: string;
  smsSender: string;
  smsTokenSet: boolean;
};

export async function fetchCallerSmsSettings(): Promise<CallerSmsSettings> {
  const r = await axios.get(`${API_URL}/api/telephony/sms`, { headers: auth() });
  return r.data as CallerSmsSettings;
}

/** A blank `smsToken` means "keep the stored one". */
export async function saveCallerSmsSettings(
  body: Omit<CallerSmsSettings, 'smsTokenSet'> & { smsToken?: string },
): Promise<CallerSmsSettings> {
  const r = await axios.put(`${API_URL}/api/telephony/sms`, body, { headers: auth() });
  return r.data as CallerSmsSettings;
}
